import React from 'react'
import styled from 'styled-components'

import { legalAgreementIds } from '../../../../utils/legalAgreements'
import { ClickWrapContentComponent } from '../../clickwrap/types'
import * as Styled from './styled'

const PLAgreementContent: ClickWrapContentComponent<{ className?: string }> = ({ className }) => {
  return (
    <Styled.ContentContainer className={className}>
      <h1>Private Label Agreement</h1>
      <p>
        THIS PRIVATE LABEL AGREEMENT BETWEEN YOU (“<strong>CUSTOMER</strong>” OR “
        <strong>YOU</strong>”) AND DESIGNS FOR HEALTH, INC. (“<strong>DFH</strong>”) TAKES EFFECT
        WHEN YOU CLICK THE “I AGREE” BUTTON BELOW (“<strong>EFFECTIVE DATE</strong>”). BY CLICKING
        ON THE “I AGREE” BUTTON AT THE BOTTOM OF THIS AGREEMENT, YOU: (A) ACKNOWLEDGE THAT YOU HAVE
        READ AND UNDERSTAND THIS AGREEMENT; (B) REPRESENT AND WARRANT THAT YOU ENTER INTO THIS
        AGREEMENT ON BEHALF OF YOURSELF IN YOUR INDIVIDUAL CAPACITY AS A NATURAL PERSON, AND NOT AS
        A BUSINESS ENTITY OR ON BEHALF OF A BUSINESS ENTITY; (C) REPRESENT AND WARRANT THAT YOU, AS
        A NATURAL PERSON, AND NOT AS A BUSINESS ENTITY OR ON BEHALF OF A BUSINESS ENTITY, ARE THE
        PRIVATE LABEL ACCOUNT HOLDER; AND (C) ACCEPT THIS AGREEMENT AND AGREE THAT YOU ARE LEGALLY
        BOUND BY ITS TERMS.
      </p>
      <p>
        IF YOU DO NOT ACCEPT THESE TERMS, YOU MAY NOT ESTABLISH A PRIVATE LABEL ACCOUNT TO PURCHASE
        PRODUCTS (DEFINED BELOW).
      </p>
      <p>
        1. <span className="text-underline">Applicability, Scope</span>. This Private Label
        Agreement incorporates by reference the Terms of Sale – Wholesale Accounts (“
        <strong>Terms of Sale</strong>”), available at{' '}
        <a href="https://www.designsforhealth.com/terms-of-sale-wholesale-accounts">
          https://www.designsforhealth.com/terms-of-sale-wholesale-accounts
        </a>{' '}
        or such other URL as DFH may designate from time to time. Unless otherwise defined herein,
        all capitalized terms shall have the same meaning as defined in the Terms of Sale. This
        Private Label Agreement shall prevail over the Terms of Sale to the extent the two documents
        (together, the “<strong>Agreement</strong>”) conflict with one another.
      </p>
      <p>
        2. <span className="text-underline">Private Label Program</span>.
      </p>
      <p>
        A. <span className="text-italic">Private Label Products</span>.
      </p>
      <p className="text-indent">
        <p>
          (1) <span className="text-italic">Nature of the Products</span>. DFH agrees to supply
          certain Designs for Health® products (“<strong>DFH Products</strong>”), Designs for
          Sports® products (“<strong>DFS Products</strong>”) and/or custom products of Customer as
          private label products (collectively, “<strong>Products</strong>”) under Customer’s brand
          and to create an <strong>Account</strong> through which Customer may purchase such
          Products, in each case under the terms and conditions of the Agreement. DFH may determine
          in its sole discretion which DFS Products, DFH Products, and custom products it will make
          available to Customer.
        </p>
        <p>
          (2) <span className="text-italic">Product Changes</span>. DFH may, at any time in its sole
          discretion, discontinue the availability or change the formula of any Product. DFH shall
          make commercially reasonable efforts to notify Customer of changes to the Regulated Copy
          (defined below in Section 2.C.(2)) on the labels of affected Products.
        </p>
      </p>
      <p>
        B. <span className="text-italic">Territory</span>. Customer shall not sell, deliver, or
        market Products, or cause Products to be sold, delivered, or marketed, anywhere except the
        United States of America (“<strong>Territory</strong>”).
      </p>
      <p>
        C. <span className="text-italic">Parties’ Roles in Label Design</span>.
      </p>
      <p className="text-indent">
        <p>
          (1) <span className="text-italic">Label Design Process, Fees</span>. Fees for the setup or
          revision of each Product label design template apply in all cases, even if Customer uses
          its own graphic designer, and will be billed in accordance with DFH’s standard rate sheet,
          which fees may change from time to time. If DFH provides label artwork files to Customer,
          use of such files shall be strictly limited to label design purposes.
        </p>
        <p>
          (2) <span className="text-italic">Regulated Copy and Marketing Copy</span>. DFH shall
          provide the label elements required by the applicable FDA regulations, such as the
          statement of identity, statement of net weight/contents, supplement facts or nutrition
          facts, ingredient statement, and allergen statement (collectively, “
          <strong>Regulated Copy</strong>”). Except as otherwise provided below in Section 2.D., all
          other label elements, including without limitation, the content and branding, Product
          names, Trademarks (defined below in Section 2.G.(1)), taglines, product claims, use or
          intake instructions, Prop 65 Warnings (defined in the Section 2.D.(1)), and the
          “Distributed By” Statement (defined below in Section 2.C.(4)) (collectively, “
          <strong>Marketing Copy</strong>”) shall be reasonably determined by and be the sole
          responsibility of Customer. DFH shall print and affix each label to each Product. Customer
          is not authorized to print, alter or affix Product labels.
        </p>
        <p>
          (3){' '}
          <span className="text-italic">Liability for Marketing Copy and Marketing Materials</span>.
          DFH may recommend Marketing Copy or Customer may source Marketing Copy from DFH, but
          regardless of whether DFH provides recommendations, review, or feedback on Marketing Copy,
          and notwithstanding anything to the contrary in DFH’s Product Warranty (defined below in
          Section 7.A.(1)), Customer remains solely responsible for ensuring that Marketing Copy and
          its business practices, website, mobile applications, advertising and other marketing
          materials (collectively, “<strong>Marketing Materials</strong>”) comply with all
          applicable <strong>Laws</strong>. Customer agrees to seek the advice of qualified legal
          counsel to confirm the compliance of Marketing Copy and Marketing Materials with all
          applicable Laws.
        </p>
        <p>
          (4) <span className="text-italic">“Distributed By” Statement</span>. Customer shall
          provide for each Product label a “distributed by” statement as required by applicable Law
          (“<strong>‘Distributed By’ Statement</strong>”). If Customer fails to do so, then DFH
          reserves the right to use, for such “Distributed By” Statement, any name(s) and/or street
          address(es) that Customer provided when creating Customer’s Account.
        </p>
      </p>
      <p>
        D. <span className="text-italic">Prop 65</span>.
      </p>
      <p className="text-indent">
        <p>
          (1) <span className="text-italic">Customer’s Prop 65 Duties</span>. In the event Customer
          sells Products in California or sells Products to third parties who resell the Products in
          California, Customer shall ensure compliance with all labeling, warning, and other
          requirements of the California Safe Drinking Water and Toxic Enforcement Act of 1986 and
          its regulations (as amended), better known as Proposition 65 (“<strong>Prop 65</strong>”).
          Customer shall provide proper Prop 65 warning statements on Prop 65 Product labels, on
          Product listings online where Customer sells or causes to be sold any Prop 65 Products,
          and as otherwise required by Prop 65 (collectively, “<strong>Prop 65 Warnings</strong>”).
        </p>
        <p>
          (2) <span className="text-italic">Prop 65 Product List</span>. DFH will maintain a list of
          Prop 65 Products and, as a courtesy, communicate updates to this list to Customer
          periodically, but Customer is solely responsible for ensuring Customer has the most
          up-to-date list of Prop 65 Products so that Customer can comply with Customer’s obligation
          to provide proper Prop 65 Warnings hereunder. For a complete list of Prop 65 Products,
          Customer may inquire with DFH Customer Experience at (860) 623-6314,{' '}
          <a href="mailto:support@designsforhealth.com">support@designsforhealth.com</a>, or such
          other means of contact as DFH may designate.
        </p>
        <p>
          (3) <span className="text-italic">Affixing Prop 65 Warnings to Products</span>. It is
          DFH’s standard policy to affix Prop 65 Warnings to all Prop 65 Products shipped by DFH to
          a California shipping address. DFH may include the statement “Not Labeled for Sale in
          California” or similar language on the label of Prop 65 Products not shipped to a
          California shipping address. DFH recommends that Customer place a Prop 65 Warning sticker
          over the “Not Labeled for Sale in California” language on the label of Prop 65 Products
          that the Customer ships or causes to be shipped to California.
        </p>
      </p>
      <p>
        E. <span className="text-italic">No Reference to “Designs for Health,” Etc</span>. Customer
        shall not, without DFH’s prior written consent, use or make any reference to “Designs for
        Health” or any Designs for Health-branded products or product names on the packaging or
        labels for any Product, as keywords for online searches, or in any mobile applications,
        advertising, product listing or any other communications or Marketing Materials.
      </p>
      <p>
        F. <span className="text-italic">Adverse Events; Product Inquiries/Complaints</span>.
        Customer shall provide DFH with a phone number for the Product labels where users of the
        Products may report adverse events or make Product inquiries or complaints. Customer shall
        promptly report to DFH, by calling DFH Customer Service at 1-860-623-6314, each adverse
        event or complaint about a Product or its use of which Customer becomes aware. Customer
        shall immediately report any such serious adverse event to DFH so DFH may comply with
        applicable Food &amp; Drug Administration (“<strong>FDA</strong>”) regulations and other
        regulations, as applicable, concerning investigation and reporting of such serious adverse
        events. Customer agrees to cooperate and provide reasonable assistance to DFH in the
        investigation of any adverse event or complaint, and to otherwise fulfill any reporting
        obligations required by Law.
      </p>
      <p>
        G. <span className="text-italic">Trademarks</span>.
      </p>
      <p className="text-indent">
        <p>
          (1) <span className="text-italic">“Trademarks” and “DFH Trademarks” Defined</span>. “
          <strong>Trademarks</strong>” means all rights in and to U.S. and foreign trademarks,
          service marks, trade dress, trade names, brand names, logos, names, images, corporate
          names and internet domain names, whether registered or unregistered. “
          <strong>DFH Trademarks</strong>” means Trademarks owned by DFH.
        </p>
        <p>
          (2){' '}
          <span className="text-italic">
            Use of Designs for Sport® Trademark Required on DFS Product Labels
          </span>
          . Each DFS Product label will include in a prominent position the DFH Trademark “Powered
          by <em>Designs for Sport®</em>” or a similar tagline or statement to be determined by DFH
          in its sole discretion. In addition, each DFS Product label will include the following
          Trademark attribution statement, “<em>Designs for Sport</em>® is a registered trademark
          of <em>Designs for Health, Inc</em>. Used with permission.” No other use of DFH Trademarks
          is permitted without DFH’s prior written consent, which it may grant or refuse in its sole
          discretion, and any such use shall be in strict accordance with any terms and conditions
          accompanying such consent.
        </p>
        <p>
          (3) <span className="text-italic">Trademark-related Prohibitions</span>. Customer shall
          not: (i) use any Trademark that is confusingly similar to a DFH Trademark as reasonably
          determined by DFH; (ii) register or apply for registrations anywhere in the world for a
          DFH Trademark or any other Trademark that, in DFH’s reasonable judgment, is confusingly
          similar to a DFH Trademark or that incorporates a DFH Trademark in whole or in part; (iii)
          engage in any action or make any statement that tends to disparage, dilute the value of,
          or otherwise reflect negatively on the Products, DFH products, DFH or any DFH Trademark;
          or (iv) challenge any right, title or interest of DFH in or to Non-Customer Intellectual
          Property (defined below in Section 6.B.) or take any action that may interfere with any of
          DFH’s rights in or to Non-Customer Intellectual Property or DFH’s exercise thereof. In the
          event Customer registers or applies to register any Trademark in violation of subsection
          (ii) of this paragraph, Customer shall immediately abandon any such registration or
          application or, upon DFH’s written demand, assign it to DFH at Customer’s expense.
          Customer shall reimburse DFH for all the costs and expenses incurred by DFH in furtherance
          of any opposition, cancellation, or related legal proceeding, including without limitation
          attorneys’ fees, in connection with any such registration or application.
        </p>
        <p>
          (4) <span className="text-italic">Third-Party Trademarks</span>. DFH is subject to various
          third-party Trademark licenses and DFH may make, but is not required to make, such
          third-party Trademarks available for use by Customer on Product labels. Unless Customer
          has a separate trademark license for a third-party trademark, DFH may remove third-party
          Trademarks from Product labels at any time in its sole discretion. In the event DFH agrees
          to include such third-party Trademarks on Product labels, Customer shall not alter or
          modify such trademarks or related Trademark label statements. Customer is not authorized
          to use any third-party Trademarks included on Product labels in any other Marketing
          Materials without the prior written consent of DFH.
        </p>
      </p>
      <p>
        3. <span className="text-underline">Prices</span>.
      </p>
      <p>
        A. <span className="text-italic">Prices to Customer</span>. Prices for sales of Products
        from DFH to Customer shall each be as set forth on the U.S. Private Label Wholesale Price
        Lists for Designs for Health® or Designs for Sport® branded Products, as applicable, and
        as they may be modified and distributed by DFH from time to time in its sole discretion
        (each such price list, as applicable, the “<strong>Price List</strong>”), subject to
        shipping and handling charges applicable to DFH’s private label customers. No discounts,
        rebates, or other pricing incentives apply unless agreed to in a writing signed by an
        authorized representative of DFH. Unless otherwise indicated, all prices for sales of
        Products from DFH to Customer shall be in U.S. Dollars. All prices are subject to change by
        DFH in its sole discretion upon written notice to Customer. Except as may be otherwise
        indicated during <strong>Checkout</strong>: (i) all Product prices, including without
        limitation the prices printed in the Price List, are exclusive of all sales, use and excise
        taxes, and any other similar taxes, duties and charges of any kind imposed by any
        governmental authority on any amounts payable by Customer; and (ii) Customer shall be
        responsible for all such charges, costs and taxes, except taxes on DFH’s income, revenues,
        gross receipts, personnel or real or personal property or other assets.
      </p>
      <p>
        B. <span className="text-italic">Prices to Customer’s customers</span>. Resale pricing for
        the Products is in the sole discretion of Customer, except that Customer shall not list,
        offer for sale, or sell the Products on any Third-Party E-Commerce Platform (defined below)
        at a resale price that is below DFH’s suggested online retail price for private label
        products, as set forth on the Price List or as otherwise communicated by DFH to Customer in
        writing. “<strong>Third-Party E-Commerce Platform</strong>” means any website or mobile
        application that is not owned or operated exclusively by or on behalf of Customer.
      </p>
      <p>
        4. <span className="text-underline">Term, Termination, Suspension</span>.
      </p>
      <p>
        A. <span className="text-italic">Term</span>. The term of this Agreement shall begin on the
        Effective Date and continue until terminated by either Party in accordance with this Section
        4 (“<strong>Term</strong>”).
      </p>
      <p>
        B. <span className="text-italic">Termination for Any Reason</span>. DFH may terminate this
        Agreement for any reason upon thirty (30) days’ prior written notice to Customer, and
        Customer may terminate this Agreement for any reason upon ninety (90) days’ prior written
        notice to DFH (collectively, “<strong>Termination for Convenience</strong>”).
      </p>
      <p>
        C. <span className="text-italic">Termination for Cause</span>. In addition to any remedies
        that may be provided in this Agreement, at law or in equity, DFH may terminate this
        Agreement with immediate effect (“<strong>Termination for Cause</strong>”) upon written
        notice to Customer, if Customer: (i) fails to pay any amount when due under this Agreement
        and such failure continues for ten (10) days after written notice of nonpayment; (ii) has
        not otherwise performed or complied with any of the terms of this Agreement, in whole or in
        part, and has not cured such non-performance or non-compliance within fourteen (14) days
        after written notice from DFH; or (iii) becomes insolvent, files a petition for bankruptcy
        or commences or has commenced against it proceedings relating to bankruptcy, receivership,
        reorganization or assignment for the benefit of creditors.
      </p>
      <p>
        D. <span className="text-italic">Events Upon Termination</span>. Upon termination: (i) DFH
        in its sole discretion may reject, cancel, fulfill, or require full or partial prepayment
        for any open Orders for which Customer has not fully paid DFH as of the effective date of
        Termination; (ii) DFH shall otherwise immediately cease performing any services hereunder;
        (iii) All amounts owed to DFH, if any, shall immediately become due and payable,
        notwithstanding any payment terms or due dates to the contrary to which the Parties may have
        agreed; (iv) DFH shall ship to Customer, at Customer’s expense, any Products remaining in
        DFH’s inventory that have been paid for in full by Customer, except to the extent any other
        payables from Customer are due and outstanding.
      </p>
      <p>
        E. <span className="text-italic">Suspension</span>. In addition to any remedies that may be
        provided in this Agreement, at law or in equity, if Customer breaches this Agreement, then
        DFH reserves the right not to accept Orders until such time as the breach is fully cured.
      </p>
      <p>
        5. <span className="text-underline">Confidential Information</span>. All non-public,
        confidential or proprietary information of DFH, including, but not limited to,
        specifications, samples, formulas, recipes, designs, plans, documents, data, business
        operations, customer lists, pricing, discounts or rebates, disclosed by DFH to Customer,
        whether disclosed orally or disclosed or accessed in written, electronic or other form or
        media, and whether or not marked, designated or otherwise identified as “confidential,” in
        connection with this Agreement is confidential, solely for the use of performing this
        Agreement and may not be used, disclosed or copied unless authorized by DFH in writing. Upon
        DFH’s request, Customer shall promptly return all documents and other materials received
        from DFH. DFH shall be entitled to injunctive relief for any violation of this Section 5.
        This Section shall not apply to information that is: (a) in the public domain; (b) known to
        the Customer at the time of disclosure supported by reasonable written evidence; (c)
        rightfully obtained by the Customer on a non-confidential basis from a third party; (d)
        disclosed after obtaining DFH’s written consent to such disclosure; or (e) required to be
        produced by Customer in a legal proceeding in a court of competent jurisdiction, provided
        that Customer must (i) promptly notify DFH of the same, (ii) help seek a protective order or
        other confidentiality measures, and (iii) only disclose the required portion(s) of such
        confidential information.
      </p>
      <p>
        6. <span className="text-underline">Intellectual Property; Licensing</span>.
      </p>
      <p>
        A. <span className="text-italic">Intellectual Property Defined</span>. “
        <strong>Intellectual Property</strong>” means all industrial and other intellectual property
        rights including without limitation patents, Trademarks, copyrights and copyrightable works,
        and trade secrets, whether registered or unregistered.
      </p>
      <p>
        B. <span className="text-italic">Retention of Rights, Goodwill</span>. Except to the extent
        provided in a separate written agreement between the Parties or as otherwise set forth
        herein, DFH or third-party licensors own and retain all Intellectual Property rights used to
        create, that are embodied in, or used in the manufacture or labeling of or otherwise
        relating to the Products (“<strong>Non-Customer Intellectual Property</strong>”). Customer
        acquires no ownership interest in or to any Non-Customer Intellectual Property under this
        Agreement. Customer is the owner of and retains all Intellectual Property rights in its
        private label brand name and any Marketing Copy created by Customer or on behalf of Customer
        and/or Trademarks created by Customer or on behalf of Customer by a third party (“
        <strong>Customer Intellectual Property</strong>”). Any and all rights and goodwill in any
        Intellectual Property inures solely to the benefit of the owner of such Intellectual
        Property. Each Party shall use the other Party’s Intellectual Property only in accordance
        with this Agreement.
      </p>
      <p>
        C. <span className="text-italic">Customer’s License to DFH</span>. Customer hereby grants
        DFH a non-exclusive, royalty-free, revocable, limited license to use Customer Intellectual
        Property solely for the purpose of manufacturing and distributing Products for Customer in
        accordance with this Agreement.
      </p>
      <p>
        7. <span className="text-underline">Representations and Warranties</span>.
      </p>
      <p>
        A. <span className="text-italic">Product Warranty</span>.
      </p>
      <p className="text-indent">
        <p>
          (1) <span className="text-italic">Warranty on Regulated Copy</span>. DFH warrants for a
          period of one (1) year from the date of manufacturing that at the time of delivery the
          Regulated Copy on Product labels complies with the applicable Laws of the U.S. This
          warranty supplements rather than replaces DFH’s warranties in the Terms of Sale regarding
          Products’ safety, clear title, and compliance with Law and specifications, which together
          with the warranty herein shall be referred to in this Agreement as the “
          <strong>Product Warranty</strong>.”
        </p>
        <p>
          (2) <span className="text-italic">Deadline for Warranty Claims</span>. DFH shall not be
          liable for a breach of the Product Warranty unless Customer gives DFH reasonably detailed
          notice of such breach within the shorter of: (i) five (5) business days after Customer
          discovers or ought to have discovered the non-conformance; or (ii) such applicable
          deadline as may be provided by the Terms of Sale.
        </p>
      </p>
      <p>
        B. <span className="text-italic">Customer’s Representations and Warranties</span>.
      </p>
      <p className="text-indent">
        <p>
          (1) <span className="text-italic">Generally</span>. Customer represents and warrants for
          itself and its employees, agents and independent contractors that: (i) custom Products,
          including without limitation their ingredients, formulation, and Marketing Copy, shall
          comply with all applicable Laws; (ii) it shall not make any false or misleading
          representations or claims regarding the Products; (iii) it operates and will continue to
          operate its business, and it shall handle, store, market, distribute and sell the
          Products, in compliance with all applicable Laws; (iv) it will store and transport the
          Products in a manner that ensures the Products’ safety and efficacy; (v) any protocols,
          dosage, recommendations and/or usage instructions for the Products provided by Customer to
          its patients or consumers, including without limitation to combine intake of the Products
          with other Products or other dietary supplements or drug products, are safe and present no
          health or safety hazard to such patients or consumers; (vi) it shall not make any
          representations or warranties regarding the Products that are prohibited under the Laws in
          the Territory; (vii) it shall comply with all applicable Laws relating to anti-bribery and
          anti-corruption, including without limitation the U.S. Foreign Corrupt Practices Act;
          (viii) the person who accepts this Agreement has been duly authorized to do so on behalf
          of Customer by all necessary action on the part of Customer; and (ix) this Agreement
          constitutes the legal, valid and binding obligation of Customer, enforceable against
          Customer in accordance with its terms.
        </p>
        <p>
          (2) <span className="text-italic">Customer Intellectual Property</span>. Customer makes a
          continuing representation, warranty, and covenant throughout the Term that, in all
          jurisdictions in which Customer sells Products or otherwise conducts business:
        </p>
        <p className="text-indent">
          <p>
            (a) Customer is the sole and exclusive legal and beneficial owner of the entire right,
            title, and interest in and to Customer Intellectual Property;
          </p>
          <p>
            (b) Customer is the record owner of all registered Trademarks constituting Customer
            Intellectual Property, and all such issued registrations are valid, subsisting, and in
            full force and effect;
          </p>
          <p>
            (c) The exercise by DFH of the rights and license to Customer Intellectual Property
            granted under this Agreement will not infringe or otherwise conflict with the rights of
            any other person or entity;
          </p>
          <p>
            (d) There is no settled, pending, or threatened litigation, opposition, or other claim
            or proceeding challenging the validity, enforceability, ownership, registration, or use
            of any Customer Intellectual Property.
          </p>
        </p>
      </p>
      <p>
        8. <span className="text-underline">Indemnification</span>.
      </p>
      <p>
        A. <span className="text-italic">Indemnification by DFH</span>. DFH shall indemnify, defend
        and hold harmless Customer and its officers, directors, employees, agents, independent
        contractors, affiliates, successors and permitted assigns (each a “
        <strong>Party Indemnified by DFH</strong>”) against any and all <strong>Losses</strong>{' '}
        incurred by a Party Indemnified by DFH directly as a result of any third-party{' '}
        <strong>Claim</strong> to the extent arising out of: (i) any claim of personal injury or
        property damage made by a third party as a result of using or consuming the Products in
        accordance with labeled instructions, except to the extent such personal injury or property
        damage is caused by Customer’s failure to comply with the terms of this Agreement or is due
        to Customer’s or its employees, agents or independent contractor’s negligent or more
        culpable act or omission; and/or (ii) any claim of a third party that Non-Customer
        Intellectual Property that is owned by DFH infringes or otherwise misappropriates any
        Intellectual Property right of such third party.
      </p>
      <p>
        B. <span className="text-italic">Indemnification by Customer</span>. Customer shall
        indemnify, defend and hold harmless the <strong>Parties Indemnified by Customer</strong>{' '}
        against any and all Losses incurred by a Party Indemnified by Customer directly as a result
        of any third-party Claim to the extent arising out of: (i) Marketing Copy and Marketing
        Materials; (ii) sales of Products by Customer or any person on Customer’s behalf outside of
        the Territory; (iii) any claim of a third party that Customer Intellectual Property,
        including without limitation Customer’s Trademarks, infringes or otherwise misappropriates
        any Intellectual Property right of such third party; (iv) each breach by Customer of
        Customer’s covenants, representations, or warranties in this Agreement; and/or (v) anything
        listed in the Terms of Sale as triggering a duty of Customer to indemnify, defend, or hold
        harmless a Party Indemnified by Customer.
      </p>
      <p>
        C. <span className="text-italic">Control of Claims</span>. The Party that is indemnifying,
        defending, or holding harmless the other Party shall have the right to control the defense
        of any Claim.
      </p>
      <p>
        9. <span className="text-underline">Insurance</span>. During the Term and for one (1) year
        thereafter, DFH shall, at its own expense, maintain and carry commercial general and
        products liability insurance in a sum no less than One Million US Dollars (US $1,000,000)
        per occurrence and Two Million US Dollars (US $2,000,000) in the aggregate with financially
        sound and reputable insurers.
      </p>
      <p>
        10. <span className="text-underline">Miscellaneous</span>.
      </p>
      <p>
        A. <span className="text-italic">Recalls</span>. DFH in its sole discretion shall determine
        whether to recall or withdraw any Product at any time. If DFH notifies Customer of a recall
        or withdrawal of a Product, then Customer—only to the extent directed to do so by DFH, and
        strictly in the manner directed by DFH, in DFH’s sole discretion—shall promptly notify all
        customers to whom or to which it shipped such recalled or withdrawn Product and coordinate
        the return or destruction of all such recalled or withdrawn Products, as well as the return
        to DFH or destruction of any recalled or withdrawn Products still in Customer’s possession.
        Customer shall document, in writing and in the ordinary course of its business operations,
        all reasonable costs and expenses incurred by Customer in connection with each Product
        recall and withdrawal, excluding Customer’s overhead costs or other fixed costs. Customer
        shall expressly identify each such cost and expense as being connected to such recall or
        withdrawal. All recalls of Products shall be handled by the Parties in accordance with
        applicable Laws, including without limitation Laws regarding “adverse event” reporting. For
        the avoidance of doubt: (i) DFH may recall or withdraw a Product even if there is no past or
        current breach of the Product Warranty associated in any way with such recall or withdrawal;
        and (ii) the fact of such a recall or withdrawal shall not, without more, constitute a
        breach of the Product Warranty or other breach of this Agreement.
      </p>
      <p>
        B. <span className="text-italic">Survival</span>. The following sections of this Private
        Label Agreement, along with each of their subsections and other subordinate elements (if
        any), survive the termination or expiration of this Agreement: 1 (“Applicability, Scope”); 2
        (“Private Label Program”), except for 2.A.(1) (“Nature of the Products”), 2.C.(2)
        (“Regulated Copy and Marketing Copy”), and 2.G. (“Trademarks”); 3 (“Prices and Ordering”); 4
        (“Term and Termination”); 5 (“Confidential Information”); 6 (“Intellectual Property;
        Licensing”); 7.A.(2) (“Deadline for Warranty Claims”); 7.B (“Customer’s Representations and
        Warranties”); 8.B. (“Indemnification by Customer”); 8.C. (“Control of Claims”); 10
        (“Miscellaneous”) and to the extent they impose obligations on DFH or grant a license to
        Customer to use any Trademark. The Terms of Sale survive the termination or expiration of
        this Agreement to the extent provided therein.
      </p>
      <p>
        C. <span className="text-italic">Waiver of Jury Trial</span>. EACH PARTY ACKNOWLEDGES AND
        AGREES THAT ANY CONTROVERSY THAT MAY ARISE UNDER THIS AGREEMENT IS LIKELY TO INVOLVE COMPLEX
        ISSUES AND, THEREFORE, EACH SUCH PARTY IRREVOCABLY AND UNCONDITIONALLY WAIVES ANY RIGHT IT
        MAY HAVE TO A TRIAL BY JURY IN RESPECT OF ANY LEGAL ACTION ARISING OUT OF OR RELATING TO
        THIS AGREEMENT OR THE TRANSACTIONS CONTEMPLATED HEREBY.
      </p>
      <p>
        D. <span className="text-italic">Notice</span>. The “Notice” section of the Terms of Sale
        applies to this Agreement, except that in addition to the addresses to which DFH, pursuant
        to the Terms of Sale, may provide notice to Customer, DFH may also send notice to Customer
        at the address(es) and/or email address(es) for Customer that are set forth in the latest
        “Distributed By” Information that Customer has provided to DFH.
      </p>
      <p>
        E. <span className="text-italic">Entire Agreement</span>. This Agreement constitutes the
        entire agreement between the Parties with respect to the subject matter hereof and
        supersedes any prior oral or written agreements between the Parties, including any previous
        Private Label Agreement between the Parties.
      </p>
      <p>
        F. <span className="text-italic">Amendments; No Waiver</span>. For the avoidance of doubt,
        no part of this Agreement may be amended or waived except as provided in the Terms of Sale.
      </p>
      <p>
        G. <span className="text-italic">No Exclusivity</span>. No relationship of exclusivity shall
        be construed from this Agreement.
      </p>
    </Styled.ContentContainer>
  )
}

PLAgreementContent.documentKey = legalAgreementIds.plOct052023

const StyledPLAgreementContent = styled(PLAgreementContent)``

export default StyledPLAgreementContent
